import { cn } from '@homecare/ui';
import { HeartPulse } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The product's mark: a rounded blue tile and the name beside it.
 *
 * One component, two surfaces. It is the first thing anyone at an agency sees
 * on the sign-in screen and the thing that sits in the corner of their screen
 * for the rest of the day, and those two have to be the same object rather than
 * two implementations that agree today.
 *
 * Only the ink changes between them. The tile — its fill, its radius, its size,
 * its glyph — is identical, which is the whole reason this is a component and
 * not a snippet copied into `auth-frame.tsx`.
 *
 * ## The tile is solid, not a gradient
 *
 * It used to carry the brand gradient. The gradient is now the measure cards'
 * top-border accent and nothing else: one decorative treatment, used in one
 * place, for one purpose. A gradient on the mark, on the primary button and on
 * the rail's active bar meant three different things wearing the same clothes,
 * and the reader has to learn which is which.
 *
 * ## No company name
 *
 * Not here and not anywhere a person at an agency can see. This is white-label
 * software: the person signing in works for a home care agency, not for the
 * vendor, and our name on their screen is a vendor concern leaking into their
 * product. The rail carries **their** agency's name underneath — which is the
 * one piece of ownership that belongs on this screen, and for someone who works
 * at two agencies is the most consequential fact on it.
 */

const INK = {
  rail: { name: 'text-rail-ink-strong', subtitle: 'text-rail-ink-muted' },
  surface: { name: 'text-ink', subtitle: 'text-ink-3' },
} as const;

export function BrandMark({
  on,
  subtitle,
  className,
}: {
  on: keyof typeof INK;
  subtitle?: ReactNode;
  className?: string | undefined;
}): ReactNode {
  const ink = INK[on];

  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span
        aria-hidden="true"
        className="bg-brand text-ink-inv h-tile w-tile grid flex-none place-items-center rounded-lg"
      >
        <HeartPulse className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className={cn('text-md block truncate font-bold', ink.name)}>Home Care</span>
        {subtitle !== undefined && (
          <span className={cn('block truncate text-xs', ink.subtitle)}>{subtitle}</span>
        )}
      </span>
    </div>
  );
}
